import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import type { ProofEntry } from 'src/shared/utils/proofUrls'

import { queryKeys } from './queryKeys'

const PROOFS_STORAGE_BUCKET = 'proofs'

function sanitizeProofUploadName(name: string): string {
  return name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(0, 100)
}

export type OrgProofBrandRow = {
  rowKey: string
  proof: ProofEntry
}

const toBrandRow = (row: {
  id: string
  url: string
  created_at: string
}): OrgProofBrandRow => ({
  rowKey: row.id,
  proof: {
    proof_id: row.id,
    url: row.url,
    status: 'pending',
    created_at: row.created_at,
  },
})

/** All `proof` rows for an org. */
export const useOrgProofsForBrandSettings = ({
  org_id,
}: {
  org_id?: string
}) => {
  const supabase = useSupabaseClient()

  const { data, isPending } = useQuery({
    queryKey: [queryKeys.proofs, 'brandSettings', org_id],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('proof')
        .select('id, url, created_at')
        .eq('org_id', org_id!)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (rows ?? []).map(toBrandRow)
    },
    enabled: !!org_id,
  })

  return { rows: data ?? [], isLoading: isPending }
}

/** Upload to storage and insert/update `proof` for the org (not linked to a quote). */
export const useUploadOrgProofForBrandSettings = ({
  org_id,
}: {
  org_id?: string
}) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (files: File[]) => {
      if (!org_id) throw new Error('Missing organization')
      if (files.length === 0) return

      const base = Date.now()

      for (let i = 0; i < files.length; i++) {
        const file = files[i]!
        const sanitizedName = sanitizeProofUploadName(file.name)
        const fileName = `${base}-${i}-${sanitizedName}`
        const filePath = `${org_id}/${fileName}`

        const { error: uploadError } = await supabase.storage
          .from(PROOFS_STORAGE_BUCKET)
          .upload(filePath, file)

        if (uploadError) throw uploadError

        const {
          data: { publicUrl },
        } = supabase.storage.from(PROOFS_STORAGE_BUCKET).getPublicUrl(filePath)

        const proofResult = await supabase
          .from('proof')
          .upsert({ org_id, url: publicUrl }, { onConflict: 'org_id,url' })
          .select('id')
          .single()

        if (proofResult.error) throw proofResult.error
      }
    },
    onSuccess: () => {
      if (org_id) {
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.proofs, 'brandSettings', org_id],
        })
      }
    },
  })
}
